import type { RegistryState } from '../data/types';
import { buildAiirRecords, type AiirRecord } from './taxOutputs';

export interface AiirCheck {
  key: string;
  label: string;
  count: number;
  severity: 'FIX' | 'INFO';
  detail: string;
}

export interface AiirReconciliation {
  reported: number;
  ledger: number;
  fromLedger: boolean;
}

const yearRange = (financialYear: string) => {
  const end = Number(financialYear.replace('FY', ''));
  return [`${end - 1}-07-01`, `${end}-06-30`];
};

export function aiirWorkspace(state: RegistryState, financialYear: string) {
  const records = buildAiirRecords(state, financialYear);
  const [from, to] = yearRange(financialYear);
  const fides = new Set(
    state.funds.filter((fund) => fund.tenantId === 't-fides').map((fund) => fund.id),
  );
  const ledgerIncome = state.ledger
    .filter(
      (entry) =>
        fides.has(entry.fundId) &&
        entry.effectiveDate >= from &&
        entry.effectiveDate <= to &&
        (entry.type === 'DISTRIBUTION' || entry.type === 'WITHHOLDING'),
    )
    .reduce((sum, entry) => sum + entry.amount, 0);
  const reported = records.reduce((sum, record) => sum + record.gross, 0);
  const count = (predicate: (record: AiirRecord) => boolean) => records.filter(predicate).length;
  const checks: AiirCheck[] = [
    {
      key: 'dob',
      label: 'Individual payees missing a date of birth',
      count: count((record) => Boolean(record.warning)),
      severity: 'FIX',
      detail: 'Ask the investors in the app; the report updates as they answer.',
    },
    {
      key: 'tfn',
      label: 'Payees with no TFN or ABN quoted',
      count: count((record) => record.identifier === 'Not quoted'),
      severity: 'INFO',
      detail: 'Tax was withheld at the top rate. They are still reported.',
    },
    {
      key: 'residency',
      label: 'Payees resident outside Australia',
      count: count((record) => record.residency !== 'AU'),
      severity: 'INFO',
      detail:
        'Reported with their country of residence. Treatment to confirm with the accountants.',
    },
  ];
  const byFund = Array.from(
    records
      .reduce((map, record) => {
        const current = map.get(record.fundId) ?? { fundId: record.fundId, payees: 0, gross: 0 };
        map.set(record.fundId, {
          ...current,
          payees: current.payees + 1,
          gross: current.gross + record.gross,
        });
        return map;
      }, new Map<string, { fundId: string; payees: number; gross: number }>())
      .values(),
  );
  const reconciliation: AiirReconciliation = {
    reported,
    ledger: ledgerIncome,
    fromLedger: ledgerIncome > 0,
  };
  return { records, checks, byFund, reconciliation };
}
